import React, { useEffect, useState } from "react"
import { Badge, Col, Divider, message, Space, Table, Tooltip } from "antd"
import { PrinterFilled } from "@ant-design/icons"
import Typography from "antd/es/typography/Typography"
import { collection, doc, onSnapshot, query, updateDoc, where } from "firebase/firestore"
import { db } from "../../../firebase/config"
import "../../../styles/OrderTable.css"
import { ASAPOrder, OrderAndPrefix } from "../../Orders/Helpers/OrderAndPrefix"
import MachineDetails from "./MachineDetails"
import MakePriorityJob from "../Orders/Action/MakePriorityJob"
import { addProjectedMeters } from "../../utils/ProjectedMeters/ProjectedMeters"
import { DndContext } from "@dnd-kit/core"
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

const { Title, Text } = Typography

const SortableRow = (props) => {
  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  })
  const style = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && {
        ...transform,
        scaleY: 1,
      }
    ),
    transition,
    cursor: "move",
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  }
  return <tr {...props} ref={setNodeRef} style={style} />
}

export default function VivaPrintingTable({
  selectedRowKeysArsoma,
  setSelectedRowKeysArsoma,
  tableLoading,
}) {
  //viva printer data
  const [vivaData, setVivaData] = useState([])
  const [vivaSequence, setVivaSequence] = useState(vivaData.map((item) => item.id))
  const [isSorting, setIsSorting] = useState(true)

  //gets array of the sequence
  useEffect(() => {
    const sequenceDocRef = doc(db, "sequence", "documentForVivaSequence")
    const unsubscribe = onSnapshot(sequenceDocRef, (doc) => {
      if (doc.exists()) {
        const sequenceData = doc.data()
        setVivaSequence(sequenceData.vivaSequence)
      }
    })
    return () => unsubscribe()
  }, [])

  //fetches the documents, then uses the sequence array to sort in correct order
  useEffect(() => {
    setIsSorting(true)
    const ref = collection(db, "orders")
    let q = query(
      ref,
      where("orderStatus", "==", 4),
      where("selectedProduct.machine", "==", "Viva")
    )

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      let orders = snapshot.docs.map((doc) => ({
        ...doc.data(),
        key: doc.id,
        id: doc.id,
      }))

      const withMeters = await addProjectedMeters(orders)

      // Sort orders based on their sequence by finding the index of each order's ID in the vivaSequence array
      const sortedOrders = withMeters.sort((a, b) => {
        return vivaSequence.indexOf(a.id) - vivaSequence.indexOf(b.id)
      })

      setVivaData(sortedOrders)
      setIsSorting(false)
    })

    return () => unsubscribe()
  }, [vivaSequence])

  //select for swaping data between tables
  const onSelectChangeArsoma = (newSelectedRowKeysArsoma) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeysArsoma)
    setSelectedRowKeysArsoma(newSelectedRowKeysArsoma)
  }

  const rowSelectionArsoma = {
    selectedRowKeysArsoma,
    onChange: onSelectChangeArsoma,
  }

  // Handle drag end
  const onDragEnd = async ({ active, over }) => {
    if (active.id !== over?.id) {
      const newData = arrayMove(
        vivaData,
        vivaData.findIndex((i) => i.key === active.id),
        vivaData.findIndex((i) => i.key === over?.id)
      )

      const newSequence = newData.map((item) => item.id)

      // Update the state with the new data
      setVivaData(newData)
      setVivaSequence(newSequence)

      // Update the sequence in Firestore
      try {
        const sequenceDocRef = doc(db, "sequence", "documentForVivaSequence")
        await updateDoc(sequenceDocRef, { vivaSequence: newSequence })
        message.success("Sequence updated!")
      } catch (error) {
        console.error("Error updating sequence:", error)
        message.error("Failed to update sequence. Please try again.")
      }
    }
  }

  const DragHandle = ({ id }) => {
    const { attributes, listeners, setNodeRef } = useSortable({ id })

    return (
      <span ref={setNodeRef} {...attributes} {...listeners} style={{ cursor: "grab" }}>
        ⋮⋮
      </span>
    )
  }

  //columns for arsoma printing cols
  const vivaPrintingCols = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "left",
      width: "12%",
      sorter: (a, b) => a.orderNumber - b.orderNumber,
      render: (value, record) => (
        <Space>
          {ASAPOrder(record)}
          {OrderAndPrefix(record)}
        </Space>
      ),
    },
    {
      title: "",
      dataIndex: "sort",
      width: 30,
      render: (_, record) => <DragHandle id={record.id} />,
    },
    {
      title: "Priority",
      width: "5%",
      dataIndex: "id",
      align: "center",
      render: (id) => <Badge color="blue" count={vivaSequence.indexOf(id) + 1} />,
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      // Create a unique list of clients for the filter dropdown
      filters: [...new Set(vivaData.map((item) => item.client.client))].map((client) => ({
        text: client,
        value: client,
      })),
      filterSearch: true, // Allow searching within the filter dropdown
      onFilter: (value, record) => record.client.client.includes(value),
      ellipsis: {
        showTitle: false,
      },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "left",
      onFilter: (value, record) => record.selectedProduct.product.productName === value,
      filterSearch: true,
      ellipsis: {
        showTitle: false,
      },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "labelCount",
      align: "left",
      width: "7%",
      sorter: (a, b) => a.labelCount - b.labelCount,
    },
    {
      title: "Manager",
      dataIndex: "user",
      align: "left",
      // Create a unique list of managers for the filter dropdown
      filters: [...new Set(vivaData.map((item) => item.user.split("@")[0]))].map((user) => ({
        text: user,
        value: user,
      })),
      onFilter: (value, record) => record.user.split("@")[0].includes(value),
      render: (user) => {
        if (typeof user === "string" && user.includes("@")) {
          return user.split("@")[0]
        }
        return user
      },
    },
    {
      title: "Width",
      dataIndex: ["selectedProduct", "labelWidth"],
      align: "left",
      width: "5%",
    },
    {
      title: "Height",
      dataIndex: ["selectedProduct", "labelHeight"],
      align: "left",
      width: "5%",
    },
    {
      title: "Material",
      dataIndex: ["selectedProduct", "material"],
      align: "left",
      // Create a unique list of materials for the filter dropdown
      filters: [...new Set(vivaData.map((item) => item.selectedProduct.material))].map(
        (material) => ({
          text: material,
          value: material,
        })
      ),
      filterSearch: true, // Allow searching within the filter dropdown
      onFilter: (value, record) => record.selectedProduct.material.includes(value),
      ellipsis: {
        showTitle: false,
      },
      render: (item) => (
        <Tooltip placement="topLeft" title={item}>
          {item}
        </Tooltip>
      ),
    },
    {
      title: "M.Width",
      dataIndex: ["selectedProduct", "formatWidth"],
      align: "left",
      sorter: (a, b) => a.selectedProduct.formatWidth - b.selectedProduct.formatWidth,
      render: (value, record) =>
        record.readRollWidth ? <Text>{record.rollWidth}</Text> : <Text>{value}</Text>,
    },
    {
      title: "Proj. M.",
      dataIndex: ["projectedMeters"],
      align: "left",
      key: "projectedMeters",
      ellipsis: true,
      width: "7%",
      render: (value, record) => <Text>{value}m</Text>,
    },

    {
      title: "Details",
      align: "center",
      width: "10%",
      render: (value) => (
        <Space>
          <MachineDetails data={value} />
          <MakePriorityJob data={value} />
        </Space>
      ),
    },
  ]

  return (
    <div>
      {/* VIVA TABLE */}
      <Col span={24}>
        <Title level={4}>
          <PrinterFilled style={{ color: "green" }} />
          <Divider type="vertical" />
          Viva Printing List
        </Title>
        <DndContext onDragEnd={onDragEnd}>
          <SortableContext
            items={vivaData.map((i) => i.key)}
            strategy={verticalListSortingStrategy}>
            <Table
              style={{ marginTop: "15px" }}
              size="small"
              loading={isSorting || tableLoading}
              columns={vivaPrintingCols}
              dataSource={vivaData}
              rowSelection={rowSelectionArsoma}
              rowKey={(record) => record.id}
              pagination={{ defaultPageSize: "12", showSizeChanger: true }}
              rowClassName={(record) => (record.priorityOrder ? "red-background" : "")}
              components={{
                header: {
                  cell: (props) => (
                    <th {...props} style={{ background: "#6abe39", color: "white" }} />
                  ),
                },
                body: {
                  row: SortableRow,
                },
              }}
            />
          </SortableContext>
        </DndContext>
      </Col>
    </div>
  )
}
